import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import type {
  AuditPlanDetailDTO,
  AuditRecordDetailDTO,
} from "@/modules/internal-audit/domain/contracts";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { AvatarPresignedUpload } from "@/shared/ui/AvatarPresignedUpload";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Button,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { formatDateForInput, parseISO } from "@/shared/lib/date-time";
import {
  AlertCircle,
  CheckSquare2,
  PlusIcon,
  TrashIcon,
  XSquare,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

type UserItem = { id: string; name: string };

type AuditRecordItemState = {
  id: string; // Temporary ID for local state
  title: string;
  description?: string | null;
  orderIndex: number;
  result: "pass" | "fail" | "needs_improvement" | null;
  notes: string;
  photoKey: string | null; // S3 key for photo
};

type AuditRecordFormProps = {
  auditPlan: AuditPlanDetailDTO;
  existingRecord?: AuditRecordDetailDTO;
  isEdit: boolean;
  onSubmit: (data: {
    auditPlanId: string;
    auditDate: Date;
    auditorId: string;
    auditScope: string;
    overallResult: "pass" | "fail" | "needs_improvement";
    summary?: string | null;
    status: "draft" | "completed";
    items: Array<{
      title: string;
      description?: string | null;
      orderIndex: number;
      result: "pass" | "fail" | "needs_improvement";
      notes?: string | null;
      photoKey?: string | null;
    }>;
    attachments: string[];
  }) => Promise<void>;
  onAutoSave?: (data: {
    auditDate: Date | null;
    auditorId: string | null;
    auditScope: string;
    items: AuditRecordItemState[];
  }) => Promise<void>;
  submitting: boolean;
};

export function AuditRecordForm({
  auditPlan,
  existingRecord,
  isEdit,
  onSubmit,
  onAutoSave,
  submitting,
}: AuditRecordFormProps) {
  const { user } = useAuthState();

  // Form state
  const [auditDate, setAuditDate] = useState(formatDateForInput(new Date()));
  const [auditorId, setAuditorId] = useState<string>("");
  const [auditScope, setAuditScope] = useState("");
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState<"draft" | "completed">("draft");
  const [items, setItems] = useState<AuditRecordItemState[]>([]);
  const [attachments, setAttachments] = useState<string[]>([]);

  let itemIdCounter = 0;

  // Initialize form from existing record or audit plan
  useEffect(() => {
    if (isEdit && existingRecord) {
      setAuditDate(formatDateForInput(existingRecord.auditDate));
      setAuditorId(existingRecord.auditorId);
      setAuditScope(existingRecord.auditScope);
      setSummary(existingRecord.summary || "");
      setStatus(existingRecord.status);
      if (existingRecord.items) {
        // For edit mode, we'll initialize items without media previews
        // Users can reassign media to items using MediaSelectionDialog
        // Since attachments aren't linked to specific items in the schema,
        // we can't automatically map them. Users will need to reassign if needed.
        setItems(
          existingRecord.items.map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description || null,
            orderIndex: item.orderIndex,
            result: item.result,
            notes: item.notes || "",
            photoKey: null, // Will be set from first attachment if any
          })),
        );
      }
      if (existingRecord.attachments && existingRecord.attachments.length > 0) {
        // For edit: we don't have item-to-attachment mapping, use attachment fileKeys
        setAttachments(
          existingRecord.attachments.map((att) => att.fileKey),
        );
      }
    } else {
      // For new records, auto-select current user as auditor
      if (user?.id) {
        setAuditorId(user.id);
      }
      // Initialize with at least one empty item
      setItems([
        {
          id: `temp-${Date.now()}`,
          title: "",
          description: null,
          orderIndex: 0,
          result: null,
          notes: "",
          photoKey: null,
        },
      ]);
    }
  }, [existingRecord, isEdit, user?.id]);

  // Calculate overall result from items
  const overallResult = useMemo<"pass" | "fail" | "needs_improvement">(() => {
    if (items.length === 0) return "pass";
    const hasFail = items.some((item) => item.result === "fail");
    if (hasFail) return "fail";
    const hasNeedsImprovement = items.some(
      (item) => item.result === "needs_improvement",
    );
    if (hasNeedsImprovement) return "needs_improvement";
    return "pass";
  }, [items]);

  // Auto-save handler with debounce
  const debouncedAutoSave = useDebounceCallback(
    useCallback(async () => {
      if (onAutoSave && !isEdit) {
        await onAutoSave({
          auditDate: auditDate ? parseISO(auditDate) : null,
          auditorId: auditorId || null,
          auditScope,
          items,
        });
      }
    }, [auditDate, auditorId, auditScope, items, onAutoSave, isEdit]),
    2000, // 2 second debounce
  );

  // Trigger auto-save on changes
  useEffect(() => {
    if (!isEdit && onAutoSave) {
      debouncedAutoSave();
    }
  }, [
    auditDate,
    auditorId,
    auditScope,
    items,
    debouncedAutoSave,
    isEdit,
    onAutoSave,
  ]);

  const handleAddItem = () => {
    itemIdCounter += 1;
    setItems((prev) => {
      // Find the max orderIndex and add 1
      const maxOrderIndex =
        prev.length > 0 ? Math.max(...prev.map((item) => item.orderIndex)) : -1;
      return [
        ...prev,
        {
          id: `temp-${Date.now()}-${itemIdCounter}`,
          title: "",
          description: null,
          orderIndex: maxOrderIndex + 1,
          result: null,
          notes: "",
          photoKey: null,
        },
      ];
    });
  };

  const handleRemoveItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleItemChange = (
    id: string,
    field: keyof AuditRecordItemState,
    value: unknown,
  ) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  };

  const handleItemPhotoChange = (itemId: string, key: string | null) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, photoKey: key } : item,
      ),
    );
  };

  const handleSubmit = async (overrideStatus?: "draft" | "completed") => {
    // Use override status if provided, otherwise use current status
    const submitStatus = overrideStatus ?? status;

    // Validate
    if (!auditorId) {
      toast.error("ກະລຸນາເລືອກຜູ້ກວດສອບ");
      return;
    }
    if (!auditScope.trim()) {
      toast.error("ກະລຸນາປ້ອນສິ່ງທີ່ກວດສອບ");
      return;
    }

    // Filter out items without title and result
    const validItems = items.filter(
      (item) =>
        item.title && item.title.trim().length > 0 && item.result !== null,
    );

    // Check for items with missing fields to provide better error messages
    const itemsWithoutTitle = items.filter(
      (item) =>
        (!item.title || item.title.trim().length === 0) && item.result !== null,
    );
    const itemsWithoutResult = items.filter(
      (item) =>
        item.title && item.title.trim().length > 0 && item.result === null,
    );
    const itemsWithoutBoth = items.filter(
      (item) =>
        (!item.title || item.title.trim().length === 0) && item.result === null,
    );

    if (validItems.length === 0) {
      if (itemsWithoutBoth.length > 0) {
        toast.error("ກະລຸນາປ້ອນຫົວຂໍ້ແລະເລືອກຜົນການກວດສອບສຳລັບລາຍການທີ່ເພີ່ມ");
      } else if (itemsWithoutTitle.length > 0) {
        toast.error("ກະລຸນາປ້ອນຫົວຂໍ້ສຳລັບລາຍການທີ່ເລືອກຜົນການກວດສອບແລ້ວ");
      } else if (itemsWithoutResult.length > 0) {
        toast.error("ກະລຸນາເລືອກຜົນການກວດສອບສຳລັບລາຍການທີ່ປ້ອນຫົວຂໍ້ແລ້ວ");
      } else {
        toast.error("ກະລຸນາກວດສອບຢ່າງໜ້ອຍ 1 ລາຍການ (ຕ້ອງມີຫົວຂໍ້ແລະຜົນການກວດສອບ)");
      }
      return;
    }

    // Validate that items with "needs_improvement" have notes
    const itemsNeedingNotes = validItems.filter(
      (item) => item.result === "needs_improvement" && !item.notes?.trim(),
    );
    if (itemsNeedingNotes.length > 0) {
      toast.error(
        `ກະລຸນາປ້ອນໝາຍເຫດສຳລັບລາຍການທີ່ຕ້ອງປັບປຸງ (${itemsNeedingNotes.length} ລາຍການ)`,
      );
      return;
    }

    // Build final items - preserve original orderIndex and sort by it
    const finalItems = validItems
      .map((item) => ({
        title: item.title.trim(),
        description: item.description || null,
        orderIndex: item.orderIndex,
        result: item.result!,
        notes: item.notes.trim() || null,
        photoKey: item.photoKey || null,
      }))
      .sort((a, b) => a.orderIndex - b.orderIndex);

    // Collect fileKeys from items for attachments
    const allAttachmentFileKeys = finalItems
      .map((item) => item.photoKey)
      .filter((key): key is string => !!key);

    // Update status state if override was provided
    if (overrideStatus) {
      setStatus(overrideStatus);
    }

    await onSubmit({
      auditPlanId: auditPlan.id,
      auditDate: parseISO(auditDate),
      auditorId,
      auditScope: auditScope.trim(),
      overallResult,
      summary: summary.trim() || null,
      status: submitStatus,
      items: finalItems,
      attachments: allAttachmentFileKeys,
    });
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4 rounded-xl border bg-card p-4">
        <h3 className="font-semibold text-lg">ຂໍ້ມູນພື້ນຖານ</h3>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div data-tourid="record-form-date">
            <Label htmlFor="auditDate">ວັນທີກວດສອບ *</Label>
            <Input
              id="auditDate"
              type="date"
              value={auditDate}
              onChange={(e) => setAuditDate(e.target.value)}
              className="mt-1"
              required
            />
          </div>

          <div data-tourid="record-form-auditor">
            <Label htmlFor="auditor">ຜູ້ກວດສອບ *</Label>
            <InfiniteCombobox<UserItem>
              queryKey={["users", "auditor"]}
              queryFn={(args) =>
                fetchLookupForInfinite(`${config.apiUrl}/users/lookup`, args)
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              value={auditorId}
              onValueChange={setAuditorId}
              placeholder="ເລືອກຜູ້ກວດສອບ"
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="auditScope">ສິ່ງທີ່ກວດສອບ *</Label>
          <Input
            id="auditScope"
            value={auditScope}
            onChange={(e) => setAuditScope(e.target.value)}
            placeholder="ຕົວຢ່າງ: ການປະຕິບັດຕາມ SOP ການຜະລິດ"
            className="mt-1"
            required
          />
        </div>

        <div>
          <Label htmlFor="summary">ສະຫຼຸບ (ທ່ານສາມາດຂ້າມໄດ້)</Label>
          <Textarea
            id="summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="ສະຫຼຸບຜົນການກວດສອບ..."
            rows={3}
            className="mt-1"
          />
        </div>
      </div>

      {/* Audit Items */}
      <div
        className="space-y-4 rounded-xl border bg-card p-4"
        data-tourid="record-form-items"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">ລາຍການກວດສອບ</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            ເພີ່ມລາຍການ
          </Button>
        </div>

        <div className="space-y-4">
          {items
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map((item, index) => (
              <div
                key={item.id}
                className="space-y-3 rounded-lg border bg-muted/30 p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="mb-2">
                      <Label>
                        ຫົວຂໍ້ #{item.orderIndex + 1} *
                        {items.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(item.id)}
                            className="ml-2 h-6 w-6 text-destructive"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </Label>
                      <Input
                        value={item.title || ""}
                        onChange={(e) =>
                          handleItemChange(item.id, "title", e.target.value)
                        }
                        placeholder="ປ້ອນຫົວຂໍ້ການກວດສອບ"
                        className="mt-1"
                        required
                      />
                    </div>

                    {item.description !== undefined && (
                      <div className="mb-2">
                        <Label>ຄຳອະທິບາຍ (ທ່ານສາມາດຂ້າມໄດ້)</Label>
                        <Textarea
                          value={item.description || ""}
                          onChange={(e) =>
                            handleItemChange(
                              item.id,
                              "description",
                              e.target.value || null,
                            )
                          }
                          placeholder="ຄຳອະທິບາຍ..."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    )}

                    {/* Result Selection - Mobile-friendly Radio Buttons */}
                    <div className="mb-3">
                      <Label className="mb-2 block">ຜົນການກວດສອບ *</Label>
                      <RadioGroup
                        value={item.result || ""}
                        onValueChange={(value) =>
                          handleItemChange(
                            item.id,
                            "result",
                            value === "pass" ||
                              value === "fail" ||
                              value === "needs_improvement"
                              ? value
                              : null,
                          )
                        }
                        className="flex flex-wrap gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="pass" id={`${item.id}-pass`} />
                          <Label
                            htmlFor={`${item.id}-pass`}
                            className="flex cursor-pointer items-center gap-2 font-normal"
                          >
                            <CheckSquare2 className="h-4 w-4 text-green-600" />
                            ຜ່ານ
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="fail" id={`${item.id}-fail`} />
                          <Label
                            htmlFor={`${item.id}-fail`}
                            className="flex cursor-pointer items-center gap-2 font-normal"
                          >
                            <XSquare className="h-4 w-4 text-red-600" />
                            ບໍ່ຜ່ານ
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="needs_improvement"
                            id={`${item.id}-needs_improvement`}
                          />
                          <Label
                            htmlFor={`${item.id}-needs_improvement`}
                            className="flex cursor-pointer items-center gap-2 font-normal"
                          >
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                            ຕ້ອງປັບປຸງ
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Notes */}
                    <div className="mb-3">
                      <Label>
                        ຫມາຍເຫດ
                        {item.result === "needs_improvement" ? (
                          <span className="text-destructive"> *</span>
                        ) : (
                          <span className="font-normal text-muted-foreground text-sm">
                            {" "}
                            (ທ່ານສາມາດຂ້າມໄດ້)
                          </span>
                        )}
                      </Label>
                      <Textarea
                        value={item.notes}
                        onChange={(e) =>
                          handleItemChange(item.id, "notes", e.target.value)
                        }
                        placeholder={
                          item.result === "needs_improvement"
                            ? "ກະລຸນາປ້ອນໝາຍເຫດສຳລັບລາຍການທີ່ຕ້ອງປັບປຸງ..."
                            : "ຫມາຍເຫດ..."
                        }
                        rows={2}
                        className="mt-1"
                        required={item.result === "needs_improvement"}
                      />
                    </div>

                    {/* Photo Upload */}
                    <div>
                      <Label>ແນບຮູບ/ສື່ (ທ່ານສາມາດຂ້າມໄດ້)</Label>
                      <div className="mt-2">
                        <AvatarPresignedUpload
                          keyPrefix="uploads/audit"
                          value={item.photoKey}
                          onChange={(key) =>
                            handleItemPhotoChange(item.id, key)
                          }
                          hint="ອັບໂຫຼດຮູບພາບ"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Submit Button */}
      <div
        className="sticky bottom-0 -mx-4 -mb-4 flex justify-end gap-2 border-t bg-background p-4"
        data-tourid="record-form-submit"
      >
        {isEdit && (
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              await handleSubmit("draft");
            }}
            disabled={submitting}
            isLoading={submitting}
          >
            ບັນທຶກເປັນຮ່າງ
          </Button>
        )}
        <Button
          type="button"
          onClick={async () => {
            await handleSubmit(isEdit ? "completed" : undefined);
          }}
          disabled={submitting}
          isLoading={submitting}
        >
          {isEdit ? "ບັນທຶກແລ້ວ" : "ບັນທຶກ"}
        </Button>
      </div>

    </div>
  );
}
